/**
 * Room Messages Hooks
 *
 * React Query hooks for room-based messaging functionality (Issue #790).
 * Follows ADR-0001 pattern: Component → Hooks → Domain API → HTTP Wrapper
 *
 * Production-safe optimistic updates with immutable cache operations.
 */

import { useRef } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import {
  messagesApi,
  type Message,
  type MessageReaction,
  type ReactionEmoji,
  type ReactionWriteResponse,
  type PinWriteResponse,
  type SendMessagePayload,
} from '../lib/api/messages';
import {
  applyDesiredMine,
  setDesiredMine,
  getDesiredMine,
  claimToggleChain,
  endToggleChain,
  overlayPendingReactions,
} from '../lib/reactionOptimism';
import {
  RequestDeduplicator,
  generateIdempotencyKey,
  createClientMessageIdMinter,
} from '../lib/deduplication';
import { useAuth } from './useAuth';
import { apiPost, type SubscriptionLevel } from '../lib/api';
import { useOroMutation, OroOperation } from './useOroMutation';

/**
 * Composite keyset cursor (#2219): created_at alone drops same-second
 * messages split across a page boundary; id disambiguates within the second.
 */
export interface RoomMessagesCursor {
  before: number;
  beforeId: string;
}

export interface MessagesPage {
  messages: Message[];
  nextCursor: RoomMessagesCursor | undefined;
  /**
   * #2674: the server-resolved 24h pinned announcement for this room, carried
   * on every page's response (the lookup is room+time scoped, independent of
   * the page cursor, so every page in a refetch holds the same value).
   * `null` = server has no current pin; `undefined` = pre-#2674 response, fall
   * back to client derivation. ClubChatPage weighs this against the derived pin
   * (newest valid candidate wins) via `pickPinnedAnnouncement` — a `null` server
   * value does not strictly suppress a fresher just-posted derived pin.
   */
  pinnedAnnouncement?: Message | null;
  /**
   * #2708: the room's TRUE pin (`rooms.pinned_message_id`) resolved to a full
   * message, or `null` when there is no pin / the target was soft-deleted.
   * Carried on every page (a room-scoped lookup independent of the page
   * cursor). `undefined` = pre-#2708 response. Authoritative — there is no
   * client-side pin derivation (unlike `pinnedAnnouncement`), so ClubChatPage
   * reads the freshest page's value directly.
   */
  pinnedMessage?: Message | null;
}

/**
 * Display-order transform (#2217).
 *
 * TanStack v5 `fetchNextPage` APPENDS pages, so the RAW cache holds pages in
 * fetch order: [newest-page, older-page, ...]. Each page is internally
 * ascending (queryFn reverses the server's newest-first order), so flattening
 * raw pages yields out-of-order history once a second page loads.
 *
 * This select reverses page order so `data.pages` is fully display-ordered
 * (oldest page → newest page, each ascending) and any consumer may flatten
 * with `pages.flatMap(p => p.messages)`.
 *
 * CONTRACT: the raw cache (getQueryData/setQueryData) stays in FETCH order —
 * cache surgery (optimistic insert, delete, rollback) must target raw pages;
 * `pages[0]` is the NEWEST raw page. Module-level for stable identity.
 */
function selectDisplayOrder(data: InfiniteData<MessagesPage>): InfiniteData<MessagesPage> {
  return {
    ...data,
    pages: [...data.pages].reverse(),
    pageParams: [...data.pageParams].reverse(),
  };
}

interface SendMessageContext {
  /**
   * The room this send TARGETED (#2221). All post-mutate cache surgery
   * anchors here, never on the hook's `roomId` closure: TanStack v5
   * propagates the latest render's options onto in-flight mutations, so
   * after a mid-flight room switch the closure names the NEW room and the
   * callbacks would operate on the wrong cache. Mirrors the native hook
   * (mobile useSendRoomMessage).
   */
  roomId: string;
  tempId: string;
}

/**
 * Hook to fetch room messages with paginated loading
 * Polls every 30 seconds for new messages
 */
export function useRoomMessages(roomId: string, limit: number = 50) {
  return useInfiniteQuery<MessagesPage, Error, InfiniteData<MessagesPage>, [string, { roomId: string }], RoomMessagesCursor | undefined>({
    queryKey: ['room-messages', { roomId }],
    /**
     * NOTE: API returns Message[] newest-first with no explicit cursor.
     * We reverse for display (oldest-first chat) and compute the composite
     * cursor (#2219: created_at + id) from the oldest message after reversal.
     */
    queryFn: async ({ pageParam }) => {
      const response = await messagesApi.getRoomMessages(
        roomId,
        limit,
        pageParam?.before,
        pageParam?.beforeId
      );

      // Messages come newest-first from API, reverse for display.
      // #989: overlay any in-flight optimistic reaction toggles on top of the
      // server aggregation so a poll landing mid-toggle can't clobber the
      // pending chip (feature doc §4.3 — the predicted review hot-spot). The
      // overlay is idempotent: once the mutation settles and the server value
      // agrees, it returns the message untouched.
      const reversed = response.messages
        .reverse()
        .map((m) => overlayPendingReactions(roomId, m));
      const oldest = reversed[0];
      return {
        messages: reversed,
        nextCursor:
          reversed.length === limit && oldest
            ? { before: oldest.created_at, beforeId: oldest.id }
            : undefined,
        // #2674: preserve undefined vs null — undefined (pre-#2674 response)
        // means "fall back to client derivation", null means "no pin".
        pinnedAnnouncement: response.pinned_announcement
          ? overlayPendingReactions(roomId, response.pinned_announcement)
          : response.pinned_announcement,
        // #2708: authoritative TRUE pin (also carries reactions).
        pinnedMessage: response.pinned_message
          ? overlayPendingReactions(roomId, response.pinned_message)
          : response.pinned_message,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined,
    select: selectDisplayOrder,
    enabled: !!roomId,
    refetchInterval: 30000, // Poll every 30 seconds
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 60_000, // 60 seconds – messages are immutable
  });
}

/**
 * Hook to send a message to a room
 * TRUE optimistic updates with safe rollback (Issue #607 pattern)
 * Messages appear instantly, then get real ID from server
 *
 * Layer 1 deduplication: Frontend request deduplication via RequestDeduplicator
 * Layer 2 deduplication: server-side KV cache keyed on the per-attempt
 * idempotency key (X-Idempotency-Key)
 * Layer 3 deduplication (#1481, wired #2381): database dedup on the stable
 * client_message_id — survives a re-send of the same preserved composer
 * content after a timed-out-but-committed POST
 */
export function useSendRoomMessage(roomId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // #2381: one minter per hook instance — pending ids survive across
  // mutate() calls so a manual retry of the same failed content reuses
  // the SAME client_message_id (per-attempt regeneration would defeat
  // Layer 3 exactly the way the per-attempt idempotencyKey does Layer 2).
  const clientMessageIds = useRef(createClientMessageIdMinter());

  return useOroMutation<{ message: Message }, SendMessagePayload, SendMessageContext>({
    mutationFn: async (payload: SendMessagePayload) => {
      // ═════ Layer 1: Frontend request deduplication ═════
      const deduplicator = RequestDeduplicator.getInstance();
      const idempotencyKey = generateIdempotencyKey(); // No userId – server-side scoped

      // Check if duplicate request already pending
      const pending = deduplicator.checkDuplicate(
        `POST /rooms/${roomId}/messages`,
        payload
      );

      if (pending) {
        console.log('[useSendRoomMessage] Returning pending request for duplicate');
        return pending.promise as Promise<{ message: Message }>;
      }

      // ═════ Layer 3 (#2381): stable client_message_id ═════
      // null only when crypto.randomUUID is unavailable — omit the field
      // rather than send a non-UUID the server would reject.
      const clientMessageId = clientMessageIds.current.mint(roomId, payload);
      const requestPayload: SendMessagePayload = clientMessageId
        ? { ...payload, client_message_id: clientMessageId }
        : payload;

      // Create request with abort controller
      const controller = new AbortController();

      const promise = apiPost<{ message: Message }>(
        `/rooms/${roomId}/messages`,
        requestPayload,
        {
          idempotencyKey,
          signal: controller.signal,
        }
      ).catch((e: Error) => {
        if (e.name === 'AbortError') {
          console.log('[useSendRoomMessage] Request aborted (duplicate detected)');
        }
        throw e;
      });

      // Register with deduplicator
      deduplicator.register(
        `POST /rooms/${roomId}/messages`,
        payload,
        promise,
        controller
      );

      return promise;
      // ═════ End Layer 1 ═════
    },

    // OPTIMISTIC UPDATE - happens BEFORE server request (instant UI)
    // Closure `roomId` is correct HERE (onMutate runs synchronously at
    // mutate() time) — it becomes context.roomId for the async callbacks.
    onMutate: async (payload): Promise<SendMessageContext> => {
      // Cancel outgoing refetches to avoid race conditions
      await queryClient.cancelQueries({ queryKey: ['room-messages', { roomId }] });

      const previousData = queryClient.getQueryData<InfiniteData<MessagesPage>>([
        'room-messages',
        { roomId },
      ]);

      // Create UNIQUE temp ID (critical: prevents duplicate message bug)
      const tempId = `temp-${Date.now()}-${
        globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)
      }`;

      // Create optimistic message with real user data for instant UX
      const optimisticMessage: Message = {
        id: tempId,
        club_id: '', // Will be filled by server
        room_id: roomId,
        user_id: user?.id || '',
        user_name: user?.public_display_name || 'You',
        // user_level removed - see #785
        subscription_level: (user?.subscription_level as SubscriptionLevel) || null,
        profile_image_url: user?.profile_image_url || null,
        club_role: 'member', // Server will correct this based on actual membership
        body: payload.body,
        image_url: payload.image_urls?.[0] || undefined,
        image_urls: payload.image_urls || null,
        created_at: Math.floor(Date.now() / 1000),
        // #1445: reflect the announcement flag optimistically so the badge and
        // the derived pin appear instantly; the server response (onSuccess swap)
        // carries the authoritative is_announcement.
        is_announcement: payload.is_announcement,
      };

      // Optimistically update cache (FULLY IMMUTABLE).
      // Raw cache is in FETCH order (#2217): pages[0] is the NEWEST page —
      // append there so the temp message flattens to the visual bottom.
      // (Appending to the last raw page would land it in the OLDEST page
      // and render mid-history once a second page is loaded.)
      if (previousData) {
        const nextPages = previousData.pages.map((page, idx) => {
          const isNewestPage = idx === 0;
          if (!isNewestPage) return page;

          // Deep clone: create new page object with new messages array
          return {
            ...page,
            messages: [...page.messages, optimisticMessage],
          };
        });

        queryClient.setQueryData<InfiniteData<MessagesPage>>(
          ['room-messages', { roomId }],
          {
            ...previousData,
            pages: nextPages,
          }
        );
      }

      return { roomId, tempId };
    },

    // Replace ONLY the matching temp message with server response — in the
    // SENT room's cache (context anchor, not the roomId closure — #2221).
    onSuccess: (response, _variables, context) => {
      if (!context?.roomId || !context.tempId) {
        console.warn('[useSendRoomMessage] onSuccess called without roomId/tempId in context');
        return;
      }

      // #2381: the send is server-confirmed — the composer moved on, so
      // clear the room's pending client_message_ids (Frank round 2: a
      // stale failed-send id must not suppress a later intentional
      // identical message); a deliberate identical send now mints fresh.
      // Before the cache guards below: they can early-return, but the
      // server has the row either way.
      clientMessageIds.current.settle(context.roomId);

      const key = ['room-messages', { roomId: context.roomId }];
      const current = queryClient.getQueryData<InfiniteData<MessagesPage>>(key);

      if (!current) return;

      // A refetch landing mid-send can swallow the temp row (cancelQueries
      // is one-shot); the swap then has no target. Warn instead of silently
      // no-oping — the settle-invalidate reconciles from the server.
      const tempPresent = current.pages.some((page) =>
        page.messages.some((msg) => msg.id === context.tempId),
      );
      if (!tempPresent) {
        console.warn('[useSendRoomMessage] temp message not found for swap:', {
          tempId: context.tempId,
          roomId: context.roomId,
        });
        return;
      }

      // Deduped double-send (#2270): two same-payload mutate() calls share
      // ONE network promise (RequestDeduplicator), but each runs its own
      // onMutate/onSuccess with the SAME response.message. If the server id
      // is already in the cache (sibling swapped first, or a poll landed it
      // mid-send), swapping again would render the same id twice — remove
      // this temp row instead.
      const serverIdPresent = current.pages.some((page) =>
        page.messages.some((msg) => msg.id === response.message.id),
      );
      if (serverIdPresent) {
        queryClient.setQueryData<InfiniteData<MessagesPage>>(key, {
          ...current,
          pages: current.pages.map((page) => ({
            ...page,
            messages: page.messages.filter((msg) => msg.id !== context.tempId),
          })),
        });
        return;
      }

      // IMMUTABLE UPDATE: Only swap the message with matching tempId
      const nextPages = current.pages.map((page) => ({
        ...page,
        messages: page.messages.map((msg) =>
          msg.id === context.tempId ? response.message : msg
        ),
      }));

      queryClient.setQueryData<InfiniteData<MessagesPage>>(key, {
        ...current,
        pages: nextPages,
      });
    },

    // Rollback on error: surgically remove the temp row from the SENT room's
    // cache. NOT a snapshot restore — a whole-cache restore discards pages
    // fetched (or polls landed) between onMutate and the failure (#2217
    // audit F3); removal undoes exactly the optimistic insert and nothing else.
    onError: (error, _variables, context) => {
      console.error('[useSendRoomMessage] Failed to send message:', error);

      if (!context?.roomId || !context.tempId) return;
      const key = ['room-messages', { roomId: context.roomId }];
      queryClient.setQueryData<InfiniteData<MessagesPage>>(key, (prev) =>
        prev
          ? {
              ...prev,
              pages: prev.pages.map((page) => ({
                ...page,
                messages: page.messages.filter((msg) => msg.id !== context.tempId),
              })),
            }
          : prev,
      );
      // Note: Error toasts handled by useOroMutation wrapper (Issue #708)
    },

    // Revalidate the SENT room from server after settle (ensures consistency)
    onSettled: (_data, _error, _variables, context) => {
      if (!context?.roomId) return;
      return queryClient.invalidateQueries({
        queryKey: ['room-messages', { roomId: context.roomId }],
      });
    },

    oro: {
      op: OroOperation.SendRoomMessage,
      retryPolicy: 'never',
      requiresIdempotency: true,
      debugLabel: 'Room chat message send',
    },
  });
}

// Stable empty reactions array so a "no reactions" message keeps reference
// identity through the cache-surgery helpers below (avoids needless renders).
const EMPTY_REACTIONS: MessageReaction[] = [];

type RoomMessagesKey = ['room-messages', { roomId: string }];

function roomMessagesKey(roomId: string): RoomMessagesKey {
  return ['room-messages', { roomId }];
}

/**
 * Apply `transform` to the `reactions` of every occurrence of `messageId` in
 * the cache — the paginated `messages[]`, the `pinnedAnnouncement`, and the
 * `pinnedMessage` (a pinned/announced message carries reactions on both its
 * list row and its pinned object). Fully immutable; preserves references where
 * `transform` is a no-op so unaffected pages/messages don't re-render.
 */
function mapMessageReactions(
  data: InfiniteData<MessagesPage>,
  messageId: string,
  transform: (reactions: MessageReaction[]) => MessageReaction[],
): InfiniteData<MessagesPage> {
  const mapOne = <T extends Message | null | undefined>(msg: T): T => {
    if (!msg || msg.id !== messageId) return msg;
    const base = msg.reactions ?? EMPTY_REACTIONS;
    const next = transform(base);
    if (next === base) return msg;
    return { ...msg, reactions: next };
  };

  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      messages: page.messages.map(mapOne),
      pinnedAnnouncement: mapOne(page.pinnedAnnouncement),
      pinnedMessage: mapOne(page.pinnedMessage),
    })),
  };
}

interface ToggleReactionVars {
  messageId: string;
  emoji: ReactionEmoji;
  /** The chip's CURRENT `mine` state; the toggle flips it. */
  mine: boolean;
}

/**
 * Result of a toggle mutation. A COALESCED toggle recorded its desired intent
 * but fired no request (a chain for the key was already running) — the running
 * chain owns the eventual cache write. A CHAIN toggle carries the FINAL server
 * response after the key converged to the last client intent.
 */
type ToggleReactionResult =
  | { coalesced: true }
  | { coalesced: false; response: ReactionWriteResponse };

/**
 * #989 — toggle the viewer's reaction on a message.
 *
 * Optimistic + poll-race overlay (feature doc §4.3): `onMutate` records the
 * desired end-state (the messages `queryFn` overlay re-applies it on any poll
 * that lands mid-flight) and writes the optimistic chip for an instant flip.
 *
 * Same-key SERIALIZATION (Frank r3 — retires the completion-order hazard): the
 * two entry points (chip row + action-sheet palette) own independent mutations,
 * so the same (message, emoji) can be toggled concurrently. Firing one HTTP
 * request per toggle let them COMPLETE out of order on the server (a slow add
 * landing after a fast remove leaves the backend reacted, and the next poll
 * reverses the user's remove). Instead the mutationFn serializes per key: the
 * first toggle claims the single in-flight slot and drives a request CHAIN that
 * re-reads the latest desired end-state after each settle and fires exactly one
 * follow-up when the server hasn't converged; concurrent toggles only update the
 * desired state and COALESCE (fire nothing). At most one request is ever on the
 * wire per key → no out-of-order completion, and the server converges to the
 * LAST CLIENT INTENT. This subsumes the r2/r3 generation-token guards (removed):
 * with one request per key there is no stale settle to clear-guard or write-guard.
 *
 * `onSuccess` writes the converged server aggregation (re-overlaying any newer
 * intent that started post-convergence) and invalidates the reactor list (Frank
 * r2); a coalesced toggle skips it. `onError` reconciles to server truth with a
 * one-shot invalidate — the chain already released the key, so the refetch isn't
 * re-overlaid with the failed desired; the useOroMutation wrapper toasts.
 */
export function useToggleReaction(roomId: string) {
  const queryClient = useQueryClient();

  return useOroMutation<ToggleReactionResult, ToggleReactionVars>({
    mutationFn: async ({ messageId, emoji }) => {
      // Serialize per (roomId, messageId, emoji). onMutate already recorded this
      // toggle's desired end-state (setDesiredMine); claim the single in-flight
      // slot. A chain already running → COALESCE (it will converge to our
      // recorded intent) and fire nothing.
      if (!claimToggleChain(roomId, messageId, emoji)) {
        return { coalesced: true };
      }
      // We own the chain: drive requests until the server matches the latest
      // desired end-state (exactly one follow-up per un-serviced flip).
      for (;;) {
        const target = getDesiredMine(roomId, messageId, emoji);
        if (target === undefined) {
          // Defensive: state vanished (shouldn't happen while we hold inFlight).
          endToggleChain(roomId, messageId, emoji);
          return { coalesced: true };
        }
        let response: ReactionWriteResponse;
        try {
          response = target
            ? await messagesApi.addReaction(roomId, messageId, emoji)
            : await messagesApi.removeReaction(roomId, messageId, emoji);
        } catch (err) {
          endToggleChain(roomId, messageId, emoji); // release the key on failure
          throw err;
        }
        // Converged iff no newer intent arrived during the request. This check
        // and endToggleChain are synchronous (no await between) so a coalescing
        // toggle can't slip a new intent into a slot we're about to drop.
        if (getDesiredMine(roomId, messageId, emoji) === target) {
          endToggleChain(roomId, messageId, emoji);
          return { coalesced: false, response };
        }
        // else: a newer intent arrived mid-request → fire exactly one follow-up.
      }
    },
    oro: { op: OroOperation.ToggleReaction, retryPolicy: 'never' },
    onMutate: async ({ messageId, emoji, mine }) => {
      const desiredMine = !mine;
      // Record the latest desired end-state SYNCHRONOUSLY (before any await) so a
      // concurrent toggle and any mid-flight poll both see it immediately.
      setDesiredMine(roomId, messageId, emoji, desiredMine);
      const key = roomMessagesKey(roomId);
      await queryClient.cancelQueries({ queryKey: key });
      const current = queryClient.getQueryData<InfiniteData<MessagesPage>>(key);
      if (current) {
        queryClient.setQueryData<InfiniteData<MessagesPage>>(
          key,
          mapMessageReactions(current, messageId, (reactions) =>
            applyDesiredMine(reactions, emoji, desiredMine),
          ),
        );
      }
    },
    onSuccess: (result, { messageId, emoji }) => {
      // Coalesced toggle: fired no request, owns no cache write — the running
      // chain writes the converged state.
      if (result.coalesced) return;

      // #2708 (Frank r2): a previously-fetched reactor list (staleTime 30s) can
      // now contradict the updated chip count. Invalidate the EXACT reactor-list
      // key for this (message, emoji) so a reopened popover refetches fresh names.
      queryClient.invalidateQueries({
        queryKey: ['reaction-users', { roomId, messageId, emoji }],
      });

      // Write the converged server aggregation, re-overlaying any NEWER intent
      // that started after the chain released the key (a fresh chain's desired
      // state) so this write can't clobber it. Nothing pending → overlay no-op,
      // raw server value stands.
      const key = roomMessagesKey(roomId);
      const current = queryClient.getQueryData<InfiniteData<MessagesPage>>(key);
      if (current) {
        queryClient.setQueryData<InfiniteData<MessagesPage>>(
          key,
          mapMessageReactions(
            current,
            messageId,
            () =>
              overlayPendingReactions(roomId, {
                id: messageId,
                reactions: result.response.reactions,
              }).reactions ?? result.response.reactions,
          ),
        );
      }
    },
    onError: (_err, { messageId, emoji }) => {
      // The request chain failed (mutationFn already released the key in its
      // catch). Server state is now uncertain — reconcile to truth with a
      // one-shot refetch. endToggleChain is an idempotent safety net so the
      // refetch isn't re-overlaid with the failed desired. The useOroMutation
      // wrapper surfaces the error toast.
      endToggleChain(roomId, messageId, emoji);
      queryClient.invalidateQueries({ queryKey: roomMessagesKey(roomId) });
    },
  });
}

/**
 * #2708 — pin (or replace the room's pin with) a message. Leader/owner only
 * (server-enforced; the UI gates the affordance on the `can_pin` room hint). No
 * optimism (a low-frequency leader action); the settle invalidation refetches
 * the messages envelope so the `pinnedMessage` banner reflects the new pin.
 */
export function usePinMessage(roomId: string) {
  const queryClient = useQueryClient();

  return useOroMutation<PinWriteResponse, { messageId: string }>({
    mutationFn: ({ messageId }) => messagesApi.pinMessage(roomId, messageId),
    oro: { op: OroOperation.PinMessage, retryPolicy: 'never' },
    successToast: 'Message pinned',
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: roomMessagesKey(roomId) });
    },
  });
}

/**
 * #989 — lazy reactor-list peek for one emoji on one message. Fetched only
 * when a chip's reactor popover opens (`enabled` guard), NOT part of the page
 * aggregation. Names are `getPublicDisplayName`-resolved server-side (#2779).
 * Requires room read access (auth) — the `enabled` guard also covers the
 * closed-popover state (no emoji selected).
 */
export function useReactionUsers(
  roomId: string,
  messageId: string,
  emoji: ReactionEmoji | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['reaction-users', { roomId, messageId, emoji }],
    queryFn: () => messagesApi.getReactionUsers(roomId, messageId, emoji!),
    enabled: enabled && !!roomId && !!messageId && !!emoji,
    staleTime: 30_000,
  });
}

/**
 * #2708 — unpin the room's pinned message. Leader/owner only. Idempotent.
 */
export function useUnpinMessage(roomId: string) {
  const queryClient = useQueryClient();

  return useOroMutation<PinWriteResponse, void>({
    mutationFn: () => messagesApi.unpinMessage(roomId),
    oro: { op: OroOperation.UnpinMessage, retryPolicy: 'never' },
    successToast: 'Message unpinned',
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: roomMessagesKey(roomId) });
    },
  });
}
